import { useState, useEffect } from 'react';
import { getRoutingConfig } from '../utils/routing-config-manager.js';
import { decryptData } from '../utils/encrypt-service.js';
import debug from '@commons-shared/debug';
import { config } from '../config/environment';

/**
 * Hook pour charger les paramètres IA complets depuis routing_config
 * Décrypte les clés API et charge les modèles + langue de travail
 * Utilisé par l'onglet IA pour afficher les clés décryptées et les modèles
 */
export const useDecryptedApiKeys = (shouldLoad = false) => {
  const [decryptedKeys, setDecryptedKeys] = useState({
    textAiApiKey: '',
    textAiModel: '',
    textWorkingLanguage: '',
    visionAiApiKey: '',
    visionAiModel: '',
    visionWorkingLanguage: '',
    translationAiApiKey: '',
    translationAiModel: '',
    translationWorkingLanguage: ''
  });
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!shouldLoad || loaded) return;

    let isMounted = true;
    
    const loadConfigFromRouting = async () => {
      setLoading(true);
      
      try {
        debug.log(config, 'Chargement des paramètres IA depuis routing_config...');
        
        const routingConfig = await getRoutingConfig();
        
        // Décrypter les clés API
        let textAiApiKey = '';
        let visionAiApiKey = '';
        let translationAiApiKey = '';
        
        try {
          const textEncrypted = routingConfig.text?.['X-API-Key'];
          if (textEncrypted) {
            const decrypted = await decryptData(textEncrypted);
            textAiApiKey = decrypted.apiKey || decrypted || '';
          }
        } catch (error) {
          debug.warn(config, 'Erreur lors du décryptage de la clé Text:', error);
        }
        
        try {
          const visionEncrypted = routingConfig.vision?.['X-API-Key'];
          if (visionEncrypted) {
            const decrypted = await decryptData(visionEncrypted);
            visionAiApiKey = decrypted.apiKey || decrypted || '';
          }
        } catch (error) {
          debug.warn(config, 'Erreur lors du décryptage de la clé Vision:', error);
        }
        
        try {
          const translationEncrypted = routingConfig.translation?.['X-API-Key'];
          if (translationEncrypted) {
            const decrypted = await decryptData(translationEncrypted);
            translationAiApiKey = decrypted.apiKey || decrypted || '';
          }
        } catch (error) {
          debug.warn(config, 'Erreur lors du décryptage de la clé Translation:', error);
        }
        
        const loadedKeys = {
          textAiApiKey,
          textAiModel: routingConfig.text?.['X-Model'] || '',
          textWorkingLanguage: routingConfig.text?.['X-Working-Language'] || '',
          visionAiApiKey,
          visionAiModel: routingConfig.vision?.['X-Model'] || '',
          visionWorkingLanguage: routingConfig.vision?.['X-Working-Language'] || '',
          translationAiApiKey,
          translationAiModel: routingConfig.translation?.['X-Model'] || '',
          translationWorkingLanguage: routingConfig.translation?.['X-Working-Language'] || ''
        };

        if (!isMounted) return;

        setDecryptedKeys(loadedKeys);
        setLoaded(true);
        debug.log(config, 'Paramètres IA chargés et décryptés depuis routing_config');
      } catch (error) {
        debug.error(config, 'Erreur lors du chargement des paramètres IA:', error);
        if (isMounted) {
          setLoaded(true);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadConfigFromRouting();

    return () => {
      isMounted = false;
    };
  }, [shouldLoad, loaded]);

  /**
   * Fonction pour mettre à jour une clé spécifique
   * @param {string} keyName - Nom de la clé (ex: 'textAiApiKey')
   * @param {string} value - Nouvelle valeur
   */
  const updateKey = (keyName, value) => {
    setDecryptedKeys(prev => ({
      ...prev,
      [keyName]: value
    }));
  };

  return {
    decryptedKeys,
    loading,
    loaded,
    updateKey
  };
};
